import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import SignatureCanvas from 'react-signature-canvas';
import { CheckCircle, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const DigitalSignature = ({ quizResults, onComplete }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    date: new Date().toISOString().split('T')[0],
    agentId: '',
    agreed: false
  });
  const [signature, setSignature] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const signaturePadRef = useRef();

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setSignature(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fullName || !formData.agentId) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.agreed) {
      toast.error('You must agree to the acknowledgment');
      return;
    }

    if (signaturePadRef.current && signaturePadRef.current.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }

    setLoading(true);

    try {
      // Get signature as data URL
      const signatureDataUrl = signaturePadRef.current.toDataURL();

      // Submit to backend
      const response = await api.post('/auri/compliance/acknowledge', {
        agent_name: formData.fullName,
        agent_id: formData.agentId,
        quiz_score: quizResults?.score || 0,
        signature: signatureDataUrl,
        date: formData.date,
        quiz_answers: quizResults?.answers || {}
      });

      if (response.data.success) {
        setSubmitted(true);
        toast.success('Acknowledgment submitted successfully!');
        
        if (onComplete) {
          onComplete({
            ...formData,
            signature: signatureDataUrl,
            timestamp: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Error submitting acknowledgment:', error);
      toast.error('Failed to submit acknowledgment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    // Download the policy manual PDF
    window.open('https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/y8oqwm21_Bespoke%20Realty%20Office%20Policies%20and%20Proceedures_Rev1_25.pdf', '_blank');
    toast.success('Policy manual download started');
  };

  if (submitted) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="bg-green-50 border-b">
          <div className="flex items-center gap-4">
            <CheckCircle className="h-12 w-12 text-green-600" />
            <div>
              <CardTitle className="text-2xl">Acknowledgment Complete!</CardTitle>
              <CardDescription className="text-base mt-2">
                Your compliance acknowledgment has been recorded.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-lg font-semibold mb-2">✅ Completed Tasks:</p>
              <ul className="space-y-1 ml-4">
                <li>• Reviewed Office Policies and Procedures Manual</li>
                <li>• Passed Compliance Quiz with {quizResults?.score}%</li>
                <li>• Signed Digital Acknowledgment</li>
              </ul>
            </div>

            <div className="flex gap-4 pt-4">
              <Button onClick={handleDownloadPDF} className="bg-[#BCA480] hover:bg-[#a89470]">
                <Download className="h-4 w-4 mr-2" />
                Download Policy Manual PDF
              </Button>
            </div>

            <p className="text-sm text-gray-600 mt-6">
              Your acknowledgment has been logged in the Compliance Sheet and Admin Dashboard. You may now proceed with your agent onboarding.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-[#BCA480]/10 to-white">
        <CardTitle className="text-2xl">Digital Acknowledgment Form</CardTitle>
        <CardDescription className="text-base">
          Please review and sign to acknowledge receipt and understanding of the Office Policies and Procedures Manual
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Acknowledgment Text */}
          <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="font-bold text-lg mb-4">Acknowledgment Statement</h3>
            <p className="text-sm leading-relaxed mb-4">
              I acknowledge that I have received, reviewed, and understand the Bespoke Realty Office Policies and Procedures Manual. I agree to comply with all policies, procedures, and guidelines outlined in the manual.
            </p>
            <p className="text-sm leading-relaxed mb-4">
              I understand that these policies may be amended at any time by the Broker, and that amendments take effect immediately with review at the next meeting. I commit to staying informed of any policy updates and maintaining compliance with all requirements.
            </p>
            <p className="text-sm leading-relaxed">
              I have successfully completed the compliance quiz with a score of <strong>{quizResults?.score}%</strong>, demonstrating my understanding of the key policies and procedures.
            </p>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="fullName">Full Legal Name *</Label>
              <Input
                id="fullName"
                required
                value={formData.fullName}
                onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                placeholder="Enter your full name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="agentId">Agent ID / License Number *</Label>
              <Input
                id="agentId"
                required
                value={formData.agentId}
                onChange={(e) => setFormData({...formData, agentId: e.target.value})}
                placeholder="Enter your agent ID"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="date">Date</Label>
            <Input
              id="date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="mt-1"
              disabled
            />
          </div>

          {/* Signature Pad */}
          <div>
            <Label>Digital Signature *</Label>
            <div className="mt-2 border-2 border-gray-300 rounded-lg bg-white">
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  className: 'w-full h-48',
                  style: { borderRadius: '0.5rem' }
                }}
                backgroundColor="rgb(255, 255, 255)"
                penColor="#434345"
                onEnd={() => setSignature(signaturePadRef.current.toDataURL())}
              />
            </div>
            <div className="flex justify-between items-center mt-2">
              <p className="text-sm text-gray-600">Sign above using your mouse or touchscreen</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearSignature}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <div className="flex items-start space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Checkbox
              id="agreed"
              checked={formData.agreed}
              onCheckedChange={(checked) => setFormData({...formData, agreed: checked})}
            />
            <div className="flex-1">
              <Label
                htmlFor="agreed"
                className="cursor-pointer text-sm leading-relaxed"
              >
                I certify that the information provided is accurate and that I have read, understood, and agree to comply with the Bespoke Realty Office Policies and Procedures Manual. I understand this acknowledgment is legally binding.
              </Label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadPDF}
            >
              <Download className="h-4 w-4 mr-2" />
              Download Manual for Records
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#BCA480] hover:bg-[#a89470] px-8"
              size="lg"
            >
              {loading ? 'Submitting...' : 'Submit Acknowledgment'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default DigitalSignature;
